import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { authorizedFetch } from "../auth";

interface ListingRow {
  contactId: string;
  callerDisplayName: string;
  dealer: string;
  headline: string;
  startedAt: string;
  durationSeconds: number;
  actionCount: number;
  status: string;
}

interface ListingResponse {
  reports: ListingRow[];
  count: number;
}

function formatDate(iso: string): string {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", timeZone: "Europe/London",
  });
}

function formatDuration(seconds: number): string {
  if (!seconds) return "\u2014";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}m ${s}s`;
}

export function ReportListPage(): React.JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ["reports"],
    queryFn: async (): Promise<ListingResponse> => {
      const resp = await authorizedFetch("/api/reports");
      if (!resp.ok) throw new Error(`API returned ${resp.status}`);
      return resp.json() as Promise<ListingResponse>;
    },
  });

  if (isLoading) return <div className="loading">Loading meetings\u2026</div>;
  if (error) return <div className="error">Failed to load: {String(error)}</div>;
  if (!data || data.reports.length === 0) {
    return <div className="card"><p>No dealer meetings have been processed yet.</p></div>;
  }

  return (
    <div className="card">
      <h2>Recent dealer meetings ({data.count})</h2>
      <ul className="report-list">
        {data.reports.map((r) => (
          <li key={r.contactId} className="report-item">
            <div>
              <Link to={`/r/${r.contactId}`} className="headline">{r.headline}</Link>
              {r.actionCount > 0 && <span className="action-count">{r.actionCount} action{r.actionCount === 1 ? "" : "s"}</span>}
              <div className="meta">
                <span>{r.callerDisplayName}</span>
                <span>&middot; {r.dealer}</span>
                <span>&middot; {formatDate(r.startedAt)}</span>
                <span>&middot; {formatDuration(r.durationSeconds)}</span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
